import { AttachMoney, CalendarToday, Numbers } from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';
import { Surplus } from 'lib/api/reports/types';
import EnhancedTable from 'lib/designSystem/Table';
import { formatDate, formatMoney } from 'lib/functions/utils';
import React from 'react';

import InfoRow from './InfoRow';

interface PaymentSectionProps {
  surplus: Surplus;
}

const PaymentSection: React.FC<PaymentSectionProps> = ({ surplus }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          backgroundColor: 'rgba(255, 152, 0, 0.08)',
          borderRadius: '8px',
          px: 1.5,
          py: 0.8,
          border: '1px solid rgba(255, 152, 0, 0.15)',
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: 'warning.main',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            textAlign: 'center',
          }}
        >
          Pago Excedente
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 6, justifyContent: 'flex-start' }}>
        <InfoRow
          label="Monto Pagado"
          value={
            surplus.surplusDocumentConciliationAmount
              ? formatMoney(surplus.surplusDocumentConciliationAmount)
              : null
          }
          maxLength={40}
          icon={
            <AttachMoney sx={{ fontSize: '0.8rem', color: 'warning.main' }} />
          }
        />
        <InfoRow
          label="Fecha Pago Total"
          value={
            surplus.surplusDocumentPaidAt
              ? formatDate(new Date(surplus.surplusDocumentPaidAt))
              : null
          }
          maxLength={40}
          icon={
            <CalendarToday sx={{ fontSize: '0.8rem', color: 'warning.main' }} />
          }
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        {surplus.surplusDocumentConciliations &&
          surplus.surplusDocumentConciliations.length > 0 &&
          surplus.surplusDocumentConciliations.map((conciliation) => (
            <Box key={conciliation.paymentRequest.id} sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ mb: 2, color: 'warning.main' }}
              >
                Devolución de pasivos {conciliation.paymentRequest.id}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                  <InfoRow
                    label="Monto a girar devolución de pasivos"
                    value={formatMoney(conciliation.paymentRequest.amount)}
                    maxLength={40}
                    icon={
                      <AttachMoney
                        sx={{ fontSize: '0.8rem', color: 'warning.main' }}
                      />
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <InfoRow
                    label="Fecha devolución de pasivos"
                    value={formatDate(
                      new Date(conciliation.paymentRequest.executedAt),
                    )}
                    maxLength={40}
                    icon={
                      <CalendarToday
                        sx={{ fontSize: '0.8rem', color: 'warning.main' }}
                      />
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <InfoRow
                    label="Documentos a devolver"
                    value={String(
                      conciliation.paymentRequest.documentsToPay.length,
                    )}
                    maxLength={40}
                    icon={
                      <Numbers
                        sx={{ fontSize: '0.8rem', color: 'warning.main' }}
                      />
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <InfoRow
                    label="Documentos aplicados"
                    value={String(
                      conciliation.paymentRequest.documentsApplied.length,
                    )}
                    maxLength={40}
                    icon={
                      <Numbers
                        sx={{ fontSize: '0.8rem', color: 'warning.main' }}
                      />
                    }
                  />
                </Grid>
              </Grid>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ mb: 2, color: 'warning.main' }}
              >
                Pasivos a devolver
              </Typography>
              <EnhancedTable
                data={conciliation.paymentRequest.documentsToPay.map(
                  (document) => ({
                    documentAmount: document.document.amount,
                    documentFolio: document.document.folio,
                    businessIdentifier: document.document.businessIdentifier,
                    stakeholderIdentifier:
                      document.document.stakeholderIdentifier,
                    amount: document.amount,
                    businessName: document.document.business.name,
                    stakeholderName: document.document.stakeholder.name,
                  }),
                )}
                dataHeaders={[
                  {
                    key: 'documentFolio',
                    label: 'Folio pasivo',
                    type: 'text',
                  },
                  {
                    key: 'businessIdentifier',
                    label: 'Rut acreedor',
                  },
                  {
                    key: 'businessName',
                    label: 'Acreedor',
                  },
                  {
                    key: 'stakeholderIdentifier',
                    label: 'Rut deudor',
                  },
                  {
                    key: 'stakeholderName',
                    label: 'Deudor',
                  },
                  {
                    key: 'documentAmount',
                    label: 'Monto documento',
                    type: 'money',
                  },
                  { key: 'amount', label: 'Monto a pagar', type: 'money' },
                ]}
                finalSumRow
                exportToFile="xlsx"
              />
              <Box sx={{ mt: 3 }}></Box>
              {conciliation.paymentRequest.documentsApplied.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ mb: 2, color: 'warning.main' }}
                  >
                    Documentos aplicados
                  </Typography>
                  <EnhancedTable
                    data={conciliation.paymentRequest.documentsApplied.map(
                      (document) => ({
                        documentAmount: document.document.amount,
                        documentFolio: document.document.folio,
                        businessIdentifier:
                          document.document.businessIdentifier,
                        stakeholderIdentifier:
                          document.document.stakeholderIdentifier,
                        amount: document.amount,
                        businessName: document.document.business.name,
                        stakeholderName: document.document.stakeholder.name,
                      }),
                    )}
                    dataHeaders={[
                      {
                        key: 'documentFolio',
                        label: 'Folio documento',
                        type: 'text',
                      },
                      {
                        key: 'businessIdentifier',
                        label: 'Rut cliente',
                      },
                      {
                        key: 'businessName',
                        label: 'Cliente',
                      },
                      {
                        key: 'stakeholderIdentifier',
                        label: 'Rut deudor',
                      },
                      {
                        key: 'stakeholderName',
                        label: 'Deudor',
                      },
                      {
                        key: 'documentAmount',
                        label: 'Monto documento',
                        type: 'money',
                      },
                      {
                        key: 'amount',
                        label: 'Monto a aplicar',
                        type: 'money',
                      },
                    ]}
                    finalSumRow
                    exportToFile="xlsx"
                  />
                </Box>
              )}
            </Box>
          ))}
      </Box>
    </Box>
  );
};

export default PaymentSection;
